import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

export function QAPanel({ announcementId, endpoint }) {
  const base = endpoint || `/announcements/${announcementId}/questions`;
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ question: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(base);
      setQuestions(data);
    } catch {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(base, form);
      toast.success(data.message || "Question submitted for review");
      setForm({ question: "" });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-line">
      <button
        onClick={() => setOpen(!open)}
        data-testid="qa-toggle-btn"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical hover:underline"
      >
        <MessageCircle className="w-4 h-4" />
        {open ? "Hide Q&A" : "Questions & Answers"}
        {questions.length > 0 && <span className="text-xs bg-clinical/10 text-clinical rounded-full px-2 py-0.5">{questions.length}</span>}
      </button>

      {open && (
        <div className="mt-4 space-y-4" data-testid="qa-panel">
          {loading && <p className="text-sm text-muted2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}

          {!loading && questions.length === 0 && (
            <p className="text-sm text-muted2">No questions yet. Be the first to ask a health worker.</p>
          )}

          <div className="space-y-3">
            {questions.map((q) => (
              <div key={q.id} data-testid="qa-item" className="bg-sand border border-line rounded-lg p-4">
                <p className="text-sm font-semibold text-ink">{q.name} asked:</p>
                <p className="text-sm text-muted2 mt-1">{q.question}</p>
                {q.answer && (
                  <div className="mt-3 pl-3 border-l-2 border-calm">
                    <p className="text-xs font-bold uppercase tracking-wider text-calm flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5" /> {q.answered_by || "Health worker"} replied
                    </p>
                    <p className="text-sm text-ink mt-1">{q.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="bg-white border border-line rounded-lg p-4 space-y-3" data-testid="qa-form">
            <p className="text-sm font-semibold text-ink flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-clinical" /> Ask a question anonymously</p>
            <textarea
              required
              rows={2}
              placeholder="Type your question…"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              data-testid="qa-question-input"
              className="w-full border border-line rounded-md px-3 py-2 text-sm outline-none focus:border-clinical"
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> No name or email needed — reviewed by a health worker before it appears.</p>
              <button
                type="submit"
                disabled={busy}
                data-testid="qa-submit-btn"
                className="inline-flex items-center gap-1.5 bg-clinical text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-clinicaldark disabled:opacity-60"
              >
                <Send className="w-4 h-4" /> {busy ? "Sending…" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
